import React, { useState, useEffect, useRef } from "react";
import Menu from "../Menu";
import { useNavigate } from "react-router-dom";

// Load Chart.js
const loadChartJS = () => {
  return new Promise((resolve) => {
    if (window.Chart) { resolve(window.Chart); return; }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js';
    script.onload = () => resolve(window.Chart);
    document.head.appendChild(script);
  });
};

/* ─── Theme function ─────────────────────────── */
const getTheme = () => ({
  bg: 'var(--bg)',
  surface: 'var(--surface)',
  surface2: 'var(--surface2)',
  border: 'var(--border)',
  text: 'var(--text)',
  textMuted: 'var(--text-muted)',
  accent: 'var(--info)',
  info: 'var(--info)',
  warning: 'var(--warning)',
  danger: 'var(--danger)',
  accent2: 'var(--info2)',
  accent3: 'var(--info3)',
});

const Schedule_COLORS = ['#f59e0b', '#3b82f6', '#a855f7', '#10b981', '#ef4444'];

/* ─── Donut Chart via SVG ─── */
const DonutChart = ({ pct, color }) => {
  const theme = getTheme();
  const r = 42, cx = 54, cy = 54;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const gap  = circ - dash;
  return (
    <svg width={108} height={108} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={theme.surface2} strokeWidth={10} />
      <circle
        cx={cx} cy={cy} r={r} fill="none"
        stroke={color} strokeWidth={10}
        strokeDasharray={`${dash} ${gap}`}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.6s ease' }}
      />
      <text
        x={cx} y={cy + 6}
        textAnchor="middle"
        fill={color}
        fontSize={16}
        fontWeight={700}
        fontFamily="'DM Sans', sans-serif"
        style={{ transform: 'rotate(90deg)', transformOrigin: `${cx}px ${cy}px` }}
      >
        {pct}%
      </text>
    </svg>
  );
};

/* ─── Bar chart (Chart.js canvas) ─── */
const ScheduleBarChart = ({ data, period }) => {
  const theme = getTheme();
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => { loadChartJS().then(() => setReady(true)); }, []);

  useEffect(() => {
    if (!ready || !canvasRef.current || !data?.length) return;
    if (chartRef.current) chartRef.current.destroy();

    // Check if this is Report2 data (has catalogid but no totalsecond) or Report8 data (has both catalogid and totalsecond)
    const hasTotalSeconds = data.some(d => d.totalsecond !== undefined);
    const isReport2Data = data.some(d => d.catalogid && d.totalsecond === undefined);
    
    let labels, datasets;

    if (isReport2Data) {
      // Report2 data - single bar for avgrunningtimeinseconds
      labels = data.map(d => d.catalogname || `Catalog ${d.catalogid}` || 'Unknown');
      const avgRunningTime = data.map(d => (d.avgrunningtimeinseconds || 0) / 3600);
      
      datasets = [
        {
          label: 'Avg Running Time (hrs)',
          data: avgRunningTime,
          backgroundColor: 'rgba(139, 92, 246, 0.6)',
          borderColor: '#8b5cf6',
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
      ];
    } else if (hasTotalSeconds) {
      // Report8 data - two bars for totalsecond and avgrunningtimeinseconds
      labels = data.map(d => d.catalogname || `Catalog ${d.catalogid}` || 'Unknown');
      const totalSeconds = data.map(d => (d.totalsecond || 0) / 3600);
      const avgRunningTime = data.map(d => (d.avgrunningtimeinseconds || 0) / 3600);

      datasets = [
        {
          label: 'Total Seconds (hrs)',
          data: totalSeconds,
          backgroundColor: 'rgba(139, 92, 246, 0.6)',
          borderColor: '#8b5cf6',
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
        {
          label: 'Avg Running Time (hrs)',
          data: avgRunningTime,
          backgroundColor: 'rgba(0, 212, 170, 0.7)',
          borderColor: '#00d4aa',
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
      ];
    } else {
      // Fallback for other data types (schedule data)
      labels = data.map(d => d.Schedulename || d.catalogname || 'Unknown');
      const scheduled = data.map(d => (d.scheduledtimeinseconds || d.totalsecond || d.totalrunningtime * 1.3 || 0) / 3600);
      const played    = data.map(d => (d.totalrunningtime || d.avgrunningtimeinseconds || 0) / 3600);

      datasets = [
        {
          label: 'Scheduled (hrs)',
          data: scheduled,
          backgroundColor: 'rgba(139, 92, 246, 0.6)',
          borderColor: '#8b5cf6',
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
        {
          label: 'Played (hrs)',
          data: played,
          backgroundColor: 'rgba(0, 212, 170, 0.7)',
          borderColor: '#00d4aa',
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
      ];
    }

    chartRef.current = new window.Chart(canvasRef.current.getContext('2d'), {
      type: 'bar',
      data: {
        labels,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
            labels: {
              color: theme.textMuted,
              font: { family: 'DM Sans', size: 12 },
              usePointStyle: true,
              pointStyle: 'rect',
              padding: 20,
            },
          },
          tooltip: {
            callbacks: {
              label: ctx => {
                const h = Math.floor(ctx.parsed.y);
                const m = Math.round((ctx.parsed.y - h) * 60);
                return ` ${ctx.dataset.label}: ${h}h ${String(m).padStart(2,'0')}m`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: 'rgba(255,255,255,0.04)', drawBorder: false },
            ticks: { color: theme.textMuted, font: { family: 'DM Sans', size: 12 } },
          },
          y: {
            grid: { color: 'rgba(255,255,255,0.06)', drawBorder: false },
            ticks: {
              color: theme.textMuted,
              font: { family: 'DM Sans', size: 11 },
              callback: v => `${v}`,
            },
            beginAtZero: true,
          },
        },
      },
    });
    return () => chartRef.current?.destroy();
  }, [ready, data]);

  const periodLabel = { 
  today: 'Today', 
  thisweek: 'This Week', 
  last7days: 'Last 7 Days', 
  last30days: 'Last 30 Days', 
  month: 'This Month', 
  all: 'All Time', 
  custom: 'Custom' 
}[period] || '';

  return (
    <div style={{
      background: theme.surface, border: `1px solid ${theme.border}`,
      borderRadius: 16, padding: '24px 28px', marginBottom: 28,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <span style={{ fontSize: 22, color: theme.accent }}>≡</span>
        <span style={{
          background: theme.surface2, border: `1px solid ${theme.border}`,
          borderRadius: 6, padding: '2px 10px',
          color: theme.text, fontWeight: 600, fontSize: 15,
        }}>Schedule Performance</span>
        <span style={{ color: theme.text, fontWeight: 600, fontSize: 15 }}>— {periodLabel}</span>
      </div>
      {!data?.length ? (
        <div style={{ textAlign: 'center', padding: '60px', color: theme.textMuted }}>No data available</div>
      ) : (
        <div style={{ height: 320 }}>
          <canvas ref={canvasRef} />
        </div>
      )}
    </div>
  );
};

/* ─── Stat box ─── */
const StatBox = ({ label, value, valueColor }) => {
  const theme = getTheme();
  return (
  <div style={{
    background: theme.surface2, borderRadius: 10, padding: '12px 16px',
    flex: 1, minWidth: 0,
  }}>
    <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </div>
    <div style={{ fontSize: 20, fontWeight: 700, color: valueColor || theme.text, fontFamily: "'DM Mono', monospace" }}>
      {value}
    </div>
  </div>
);};

/* ─── Schedule Card (Expected vs Actual) ─── */
const ScheduleCard = ({ Schedule, color, secToHHMM, isReport9, onClick }) => {
  const theme = getTheme();
  const scheduled = Schedule.scheduledtimeinseconds || Schedule.scheduletotaltime || Math.round((Schedule.totalrunningtime || 0) * 1.35);
  const played    = Schedule.totalrunningtime || Schedule.avgrunningtimeinseconds || 0;
  const pct       = scheduled > 0 ? Math.round((played / scheduled) * 100) : 0;
  const mediaType = Schedule.mediatype || (Math.random() > 0.5 ? 'video' : 'image');

  return (
    <div 
      onClick={onClick}
      style={{
        background: theme.surface, border: `1px solid ${theme.border}`,
        borderRadius: 12, padding: '12px 14px',
        display: 'flex', flexDirection: 'column', gap: 10,
        minWidth: 0,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Title row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              background: color, display: 'inline-block', flexShrink: 0,
            }} />
            <span style={{ fontWeight: 700, fontSize: 15, color: theme.text }}>
              {Schedule.catalogname || Schedule.Schedulename}
            </span>
          </div>
          <span style={{
            background: mediaType === 'video' ? 'rgba(59,130,246,0.15)' : 'rgba(245,158,11,0.15)',
            color: mediaType === 'video' ? theme.info : theme.warning,
            border: `1px solid ${mediaType === 'video' ? 'rgba(59,130,246,0.3)' : 'rgba(245,158,11,0.3)'}`,
            borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 500,
            display: 'inline-flex', alignItems: 'center', gap: 3,
          }}>
            {mediaType === 'video' ? '▦' : '⊞'} {mediaType}
          </span>
        </div>
        {isReport9 && <DonutChart pct={Math.min(pct, 100)} color={color} />}
      </div>

      {/* Stats row 1 */}
      <div style={{ display: 'flex', gap: 6 }}>
        <StatBox label="Scheduled" value={secToHHMM(scheduled)} />
        <StatBox label="Played"    value={secToHHMM(played)}    valueColor={theme.accent} />
      </div>

      {/* Stats row 2 */}
      <div style={{ display: 'flex', gap: 6 }}>
        <StatBox label="Cycles"     value={(Schedule.avgcompletecyclecount || Schedule.completecyclecountsum || 0).toFixed(0)} />
        <StatBox label="Interrupts" value={(Schedule.avginterruptscount    || Schedule.interruptscountsum    || 0).toFixed(0)} valueColor={theme.danger} />
      </div>
    </div>
  );
};

/* ─── Main Dashboard ─── */
const ScheduleDashboard = () => {
  const theme = getTheme();
  const navigate = useNavigate();
  const [period, setPeriod]       = useState('thisweek');
  const [loading, setLoading]     = useState(true);
  const [Report1Data, setReport1] = useState(null);
  const [Report2Data, setReport2] = useState(null);
  const [Report3Data, setReport3] = useState(null);
  const [report7Data, setReport7] = useState(null);
  const [report8Data, setReport8] = useState(null);
  const [report9Data, setReport9] = useState(null);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [Schedules, setSchedules]   = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState('');
  const [ScheduleLoading, setScheduleLoading] = useState(false);

  const API_BASE   = 'https://api.dqr-display-tms.bonrix.in/api/SchedulerStatistics';
  const AUTH_TOKEN = getCookie("token");

  // API call for GET requests (for Schedules)
  const callGetAPI = async (url) => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'accept': 'text/plain',
          'UserId': getCookie("userid"),
          'X-Authorization': AUTH_TOKEN
        }
      });
      
      const result = await response.text();
      return JSON.parse(result);
    } catch (error) {
      console.error('Error calling GET API:', error);
      return { isresponse: false, errorcode: 'CLIENT_ERROR', errormsg: error.message };
    }
  };

  // Load Schedules from API
  const loadSchedules = async () => {
    setScheduleLoading(true);
    try {
      const response = await callGetAPI('https://api.dqr-display-tms.bonrix.in/api/CatalogSchedulerOperation/GetDistinct/name');
      if (response.isresponse && response.data) {
        setSchedules(response.data);
      }
    } catch (error) {
      console.error('Error loading Schedules:', error);
    } finally {
      setScheduleLoading(false);
    }
  };

  // Call Report7, Report8, Report9 APIs when Schedule is selected
  const handleScheduleChange = async (ScheduleId) => {
    // Prevent unnecessary calls if same schedule is selected
    if (ScheduleId === selectedSchedule) return;
    
    setSelectedSchedule(ScheduleId);
    
    // If "All Schedules" is selected, reload original Report1,2,3 data
    if (!ScheduleId) {
      await loadData();
      return;
    }

    setLoading(true);
    const payload = {
      startdate: 0,
      enddate: 0,
      scheduleid: parseInt(ScheduleId),
      isalltime: true
    };

    try {
      const [report7, report8, report9] = await Promise.all([
        fetch(`${API_BASE}/Report7`, {
          method: 'POST',
          headers: {
            'accept': 'text/plain',
            'UserId': getCookie("userid"),
            'X-Authorization': AUTH_TOKEN,
            'Content-Type': 'application/json-patch+json'
          },
          body: JSON.stringify(payload)
        }),
        fetch(`${API_BASE}/Report8`, {
          method: 'POST',
          headers: {
            'accept': 'text/plain',
            'UserId': getCookie("userid"),
            'X-Authorization': AUTH_TOKEN,
            'Content-Type': 'application/json-patch+json'
          },
          body: JSON.stringify(payload)
        }),
        fetch(`${API_BASE}/Report9`, {
          method: 'POST',
          headers: {
            'accept': 'text/plain',
            'UserId': getCookie("userid"),
            'X-Authorization': AUTH_TOKEN,
            'Content-Type': 'application/json-patch+json'
          },
          body: JSON.stringify(payload)
        })
      ]);

      const r7 = await report7.json();
      const r8 = await report8.json();
      const r9 = await report9.json();

      console.log('Report7:', r7);
      console.log('Report8:', r8);
      console.log('Report9:', r9);

      // Replace Report1, Report2, Report3 data with Report7, Report8, Report9 data
      if (r7.isresponse && r7.data) {
        setReport1(r7); // Replace Report1 with Report7 data
        setReport7(r7);
      }
      if (r8.isresponse && r8.data) {
        setReport2(r8); // Replace Report2 with Report8 data
        setReport8(r8);
      }
      if (r9.isresponse && r9.data) {
        setReport3(r9); // Replace Report3 with Report9 data
        setReport9(r9);
      }

    } catch (error) {
      console.error('Error calling reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = (p) => {
    const now = new Date();
    if (p === 'all') return { startdate: 0, enddate: 0, isalltime: true };
    if (p === 'custom') {
      if (customStartDate && customEndDate) {
        const start = new Date(customStartDate);
        const end = new Date(customEndDate);
        end.setHours(23, 59, 59, 999); // End of day
        return { 
          startdate: Math.floor(start.getTime() / 1000), 
          enddate: Math.floor(end.getTime() / 1000), 
          isalltime: false 
        };
      }
      return { startdate: 0, enddate: 0, isalltime: false };
    }
    const enddate = Math.floor(now.getTime() / 1000);
    let startdate;
    if (p === 'today') {
      startdate = Math.floor(new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() / 1000);
    } else if (p === 'thisweek') {
      // Get current week (Sunday to Saturday)
      const dayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
      const sunday = new Date(now);
      sunday.setDate(now.getDate() - dayOfWeek);
      sunday.setHours(0, 0, 0, 0);
      startdate = Math.floor(sunday.getTime() / 1000);
    } else if (p === 'week') {
      startdate = Math.floor((now.getTime() - 7 * 86400000) / 1000);
    } else if (p === 'last7days') {
      startdate = Math.floor((now.getTime() - 7 * 86400000) / 1000);
    } else if (p === 'last30days') {
      startdate = Math.floor((now.getTime() - 30 * 86400000) / 1000);
    } else {
      startdate = Math.floor((now.getTime() - 30 * 86400000) / 1000);
    }
    return { startdate, enddate, isalltime: false };
  };

  const callAPI = async (endpoint, payload) => {
    try {
      const res = await fetch(`${API_BASE}/${endpoint}`, {
        method: 'POST',
        headers: {
          'accept': 'text/plain',
          'UserId': getCookie("userid"),
          'X-Authorization': AUTH_TOKEN,
          'Content-Type': 'application/json-patch+json',
        },
        body: JSON.stringify(payload),
      });
      return JSON.parse(await res.text());
    } catch (e) {
      return { isresponse: false, errormsg: e.message };
    }
  };

  const loadData = async () => {
    setLoading(true);
    const range = getDateRange(period);
    const [r1, r2, r3] = await Promise.all([
      callAPI('Report1', range),
      callAPI('Report2', range),
      callAPI('Report3', range),
    ]);
    setReport1(r1); setReport2(r2); setReport3(r3);
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [period]);

  const secToHHMM = (s) => {
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    
    if (d > 0) {
      return `${d}d ${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
    } else if (h > 0) {
      return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
    } else if (m > 0) {
      return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
    } else {
      return `${sec}s`;
    }
  };

  const kpi = Report1Data?.data?.[0] || {};
  const r3  = Report3Data?.data || [];
  const r2  = Report2Data?.data || [];
  const isReport9Data = report9Data?.data?.length > 0;

  const TABS = [
    { key: 'today', label: 'Today' },
    { key: 'thisweek', label: 'This Week' },
    { key: 'last7days', label: 'Last 7 Days' },
    { key: 'last30days', label: 'Last 30 Days' },
    { key: 'month', label: 'This Month' },
    { key: 'all',   label: 'All Time' },
    { key: 'custom', label: 'Custom' },
  ];

  /* Google Font */
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500;700&display=swap';
    document.head.appendChild(link);
    
    // Load Schedules on component mount
    loadSchedules();
  }, []);


  return (
  <>
    <Menu />
    <div className="wrapper">
      <div className="content-wrapper">
        <div style={{ padding: '32px 24px' }}>
          {/* Schedule Selector */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <span style={{ fontSize: 18, fontWeight: 600, color: theme.text }}>Select Schedule:</span>
              <select
                value={selectedSchedule}
                onChange={(e) => handleScheduleChange(e.target.value)}
                disabled={ScheduleLoading}
                style={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: `1px solid ${theme.border}`,
                  background: theme.surface,
                  color: theme.text,
                  fontSize: 14,
                  minWidth: 200,
                  cursor: ScheduleLoading ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="">All Schedules</option>
                {Schedules.map(Schedule => (
                  <option key={Schedule.id} value={Schedule.id}>
                    {Schedule.value}
                  </option>
                ))}
              </select>
              {ScheduleLoading && (
                <span style={{ fontSize: 12, color: theme.textMuted, marginLeft: 8 }}>
                  Loading Schedules...
                </span>
              )}
            </div>
          </div>

          {/* Page header */}
          <section className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h1 className="m-0">◎ Overview Dashboard</h1>
                </div>
                <div className="col-sm-6">
                  <ol className="breadcrumb float-sm-right">
                    <li className="breadcrumb-item">
                      <a href="/dashboard" style={{ color: 'var(--primary)' }}>Home</a>
                    </li>
                    <li className="breadcrumb-item active">Overview Dashboard</li>
                  </ol>
                </div>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              {/* Period tabs */}
              <div className="card mb-3">
                <div className="card-body p-2">
                  <div className="btn-group w-100" role="group">
                    {TABS.map(t => (
                      <button
                        key={t.key}
                        type="button"
                        className={`btn ${period === t.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setPeriod(t.key)}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <div className="text-muted mt-2">Loading statistics…</div>
                </div>
              ) : (
                <>
                  {/* KPI row */}
                  <div className="row mb-4">
                    <div className="col-lg-3 col-6">
                      <div className="info-box">
                        <span className="info-box-icon bg-success">
                          <i className="fas fa-play"></i>
                        </span>
                        <div className="info-box-content">
                          <span className="info-box-text">Total Running Time</span>
                          <span className="info-box-number">{secToHHMM(kpi.totalrunningtimesum || 0)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-6">
                      <div className="info-box">
                        <span className="info-box-icon bg-danger">
                          <i className="fas fa-exclamation-triangle"></i>
                        </span>
                        <div className="info-box-content">
                          <span className="info-box-text">Total Interrupts</span>
                          <span className="info-box-number">{(kpi.interruptscountsum || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-6">
                      <div className="info-box">
                        <span className="info-box-icon bg-purple" style={{ backgroundColor: 'var(--info3)' }}>
                          <i className="fas fa-sync"></i>
                        </span>
                        <div className="info-box-content">
                          <span className="info-box-text">Complete Cycles</span>
                          <span className="info-box-number">{(kpi.completecyclecountsum || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-6">
                      <div className="info-box">
                        <span className="info-box-icon bg-info">
                          <i className="fas fa-list"></i>
                        </span>
                        <div className="info-box-content">
                          <span className="info-box-text">Distinct Schedules</span>
                          <span className="info-box-number">{kpi.distinctSchedulecount || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bar chart (Schedule Performance) */}
                  <ScheduleBarChart data={r2.length ? r2 : r3} period={period} />

                  {/* Expected vs Actual */}
                  <div style={{
                    background: theme.surface, border: `1px solid ${theme.border}`,
                    borderRadius: 16, padding: '24px 28px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                      <span style={{ fontSize: 20 }}>☰</span>
                      <span style={{ fontWeight: 700, fontSize: 17, color: theme.text }}>
                        Expected vs Actual
                        <span style={{ color: theme.textMuted, fontWeight: 400, fontSize: 14, marginLeft: 8 }}>
                          — {TABS.find(t => t.key === period)?.label}
                        </span>
                      </span>
                    </div>

                    {r3.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '60px', color: theme.textMuted }}>No Schedule data available</div>
                    ) : (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 12,
                        maxWidth: '100%',
                        overflow: 'hidden',
                      }}>
                        {r3.map((cat, i) => (
                          <ScheduleCard
                            key={i}
                            Schedule={cat}
                            color={Schedule_COLORS[i % Schedule_COLORS.length]}
                            secToHHMM={secToHHMM}
                            isReport9={isReport9Data}
                            onClick={() => {
                              const catalogId = cat.catalogid;
                              const currentScheduleId = selectedSchedule; // Get from selected schedule state
                              
                              console.log('Catalogue ID:', catalogId);
                              console.log('Selected Schedule ID:', currentScheduleId);
                              
                              if (catalogId) {
                                let url = `/catalog-statistics?id=${catalogId}`;
                                if (currentScheduleId) {
                                  url += `&scheduleId=${currentScheduleId}`;
                                }
                                console.log('Final URL:', url);
                                navigate(url);
                              } else {
                                console.log('No CatalogueId found in:', cat);
                              }
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  </>
);
  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <div style={{ padding: '32px 24px' }}>

            {/* Schedule Selector */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <span style={{ fontSize: 18, fontWeight: 600, color: theme.text }}>Select Schedule:</span>
                <select
                  value={selectedSchedule}
                  onChange={(e) => handleScheduleChange(e.target.value)}
                  disabled={ScheduleLoading}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: `1px solid ${theme.border}`,
                    background: theme.surface,
                    color: theme.text,
                    fontSize: 14,
                    minWidth: 200,
                    cursor: ScheduleLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  <option value="">All Schedules</option>
                  {Schedules.map(Schedule => (
                    <option key={Schedule.id} value={Schedule.id}>
                      {Schedule.value}
                    </option>
                  ))}
                </select>
                {ScheduleLoading && (
                  <span style={{ fontSize: 12, color: theme.textMuted, marginLeft: 8 }}>
                    Loading Schedules...
                  </span>
                )}
              </div>
            </div>

          {/* Page header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
            <span style={{ fontSize: 28, color: theme.accent }}>◎</span>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: theme.text, letterSpacing: '-0.02em' }}>
              Overview Dashboard
            </h1>
          </div>

          {/* Period tabs */}
          <div style={{
            display: 'flex', gap: 6, marginBottom: 28,
            background: theme.surface, border: `1px solid ${theme.border}`,
            borderRadius: 12, padding: 4, width: 'fit-content',
          }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => setPeriod(t.key)} style={{
                padding: '8px 20px', borderRadius: 9, border: 'none', cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13,
                background: period === t.key ? theme.accent : 'transparent',
                color:      period === t.key ? '#0f1117'   : theme.textMuted,
                transition: 'all 0.2s',
              }}>
                {t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '80px', color: theme.textMuted, fontSize: 15 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>⟳</div>
              Loading statistics…
            </div>
          ) : (
            <>
              {/* KPI row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
                {[
                  { icon: '▶', label: 'Total Running Time',  value: secToHHMM(kpi.totalrunningtimesum   || 0), color: theme.accent  },
                  { icon: '⚠', label: 'Total Interrupts',    value: (kpi.interruptscountsum  || 0).toLocaleString(), color: theme.danger  },
                  { icon: '↺', label: 'Complete Cycles',     value: (kpi.completecyclecountsum|| 0).toLocaleString(), color: theme.accent3  },
                  { icon: '⊞', label: 'Distinct Schedules',   value: kpi.distinctSchedulecount || 0,               color: theme.info    },
                ].map(k => (
                  <div key={k.label} style={{
                    background: theme.surface, border: `1px solid ${theme.border}`,
                    borderRadius: 14, padding: '20px 22px',
                    borderTop: `3px solid ${k.color}`,
                  }}>
                    <div style={{ fontSize: 22, marginBottom: 8 }}>{k.icon}</div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: k.color, fontFamily: "'DM Mono', monospace", marginBottom: 4 }}>
                      {k.value}
                    </div>
                    <div style={{ fontSize: 13, color: theme.textMuted }}>{k.label}</div>
                  </div>
                ))}
              </div>

              {/* Bar chart (Schedule Performance) */}
              <ScheduleBarChart data={r2.length ? r2 : r3} period={period} />

              {/* Expected vs Actual */}
              <div style={{
                background: theme.surface, border: `1px solid ${theme.border}`,
                borderRadius: 16, padding: '24px 28px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                  <span style={{ fontSize: 20 }}>☰</span>
                  <span style={{ fontWeight: 700, fontSize: 17, color: theme.text }}>
                    Expected vs Actual
                    <span style={{ color: theme.textMuted, fontWeight: 400, fontSize: 14, marginLeft: 8 }}>
                      — {TABS.find(t => t.key === period)?.label}
                    </span>
                  </span>
                </div>

                {r3.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px', color: theme.textMuted }}>No Schedule data available</div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 12,
                    maxWidth: '100%',
                    overflow: 'hidden',
                  }}>
                    {r3.map((cat, i) => (
                      <ScheduleCard
                        key={i}
                        Schedule={cat}
                        color={Schedule_COLORS[i % Schedule_COLORS.length]}
                        secToHHMM={secToHHMM}
                        isReport9={isReport9Data}
                        onClick={() => {
                          // Navigate to catalog statistics with catalog ID
                          const catalogId = cat.catalogid || cat.id;
                          if (catalogId) {
                            navigate(`/catalog-statistics?id=${catalogId}`);
                          }
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default ScheduleDashboard;
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}